'use client';

/**
 * YouTube / Vimeo / Cinema8 embed block. Auto-detects provider from URL.
 *
 * <ul>
 *   <li><b>YouTube / Vimeo</b>: sandboxed iframe. Privacy defaults — YT
 *       uses youtube-nocookie, Vimeo passes {@code dnt=1}. Override with
 *       {@code enableTracking}.</li>
 *   <li><b>Cinema8</b>: registers a {@code <cinema8-player>} custom
 *       element via the platform's player.js (loaded once per page).
 *       {@code display='popover'} renders a thumbnail that opens the
 *       player in a modal-style popover.</li>
 *   <li><b>Unknown URL</b>: renders a "Watch video" link so the
 *       operator's URL still does SOMETHING.</li>
 * </ul>
 *
 * <p>Cinema8 mount uses {@code innerHTML} injection (not React JSX) to
 * dodge hydration errors — the player script mutates the element's
 * children between server and client, which React's hydration check
 * would error on.
 */
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { BlockRoot } from '../_shared/BlockRoot';

export interface EmbeddedVideoProps {
  url?: string;
  aspectRatio?: '16/9' | '4/3' | '1/1' | '21/9';
  caption?: string;
  enableTracking?: boolean;
  title?: string;
  /** Cinema8-only. Default {@code 'inline'}. */
  display?: 'inline' | 'popover';
}

interface ParsedVideo {
  provider: 'youtube' | 'vimeo' | 'cinema8' | null;
  embedUrl: string | null;
  watchUrl: string | null;
  mediaId?: string;
}

function parseVideoUrl(url: string | undefined, enableTracking: boolean): ParsedVideo {
  if (!url) return { provider: null, embedUrl: null, watchUrl: null };
  try {
    const u = new URL(url);
    const host = u.hostname.replace(/^www\./, '');

    if (host === 'youtube.com' || host === 'm.youtube.com') {
      const id = u.searchParams.get('v');
      if (id) {
        const domain = enableTracking ? 'www.youtube.com' : 'www.youtube-nocookie.com';
        return { provider: 'youtube', embedUrl: `https://${domain}/embed/${id}`, watchUrl: url };
      }
    }
    if (host === 'youtu.be') {
      const id = u.pathname.slice(1).split('/')[0];
      if (id) {
        const domain = enableTracking ? 'www.youtube.com' : 'www.youtube-nocookie.com';
        return { provider: 'youtube', embedUrl: `https://${domain}/embed/${id}`, watchUrl: url };
      }
    }
    if (host === 'vimeo.com' || host === 'player.vimeo.com') {
      const parts = u.pathname.replace(/^\/+/, '').split('/').filter(Boolean);
      if (parts.length > 0 && parts[0] && /^\d+$/.test(parts[0])) {
        const id = parts[0];
        const hash = parts[1];
        const params = new URLSearchParams();
        if (hash) params.set('h', hash);
        if (!enableTracking) params.set('dnt', '1');
        const qs = params.toString() ? `?${params.toString()}` : '';
        return {
          provider: 'vimeo',
          embedUrl: `https://player.vimeo.com/video/${id}${qs}`,
          watchUrl: url,
        };
      }
    }
    if (
      host === 'cinema8.com' ||
      host === 'cinema8.site' ||
      host.endsWith('.cinema8.com') ||
      host.endsWith('.cinema8.site')
    ) {
      const m = u.pathname.match(/^\/video\/([A-Za-z0-9_-]+)/);
      if (m) {
        return {
          provider: 'cinema8',
          embedUrl: url,
          watchUrl: url,
          mediaId: m[1],
        };
      }
    }
    return { provider: null, embedUrl: null, watchUrl: url };
  } catch {
    return { provider: null, embedUrl: null, watchUrl: null };
  }
}

const CINEMA8_PLAYER_SCRIPT_SRC = 'https://static-01.cinema8.site/embed/player.js';
let cinema8PlayerScriptInjected = false;

function ensureCinema8PlayerScript() {
  if (typeof window === 'undefined') return;
  if (cinema8PlayerScriptInjected) return;
  if (document.querySelector(`script[src="${CINEMA8_PLAYER_SCRIPT_SRC}"]`)) {
    cinema8PlayerScriptInjected = true;
    return;
  }
  const s = document.createElement('script');
  s.src = CINEMA8_PLAYER_SCRIPT_SRC;
  s.async = true;
  document.body.appendChild(s);
  cinema8PlayerScriptInjected = true;
}

export function EmbeddedVideo({
  url,
  aspectRatio = '16/9',
  caption,
  enableTracking = false,
  title,
  display = 'inline',
}: EmbeddedVideoProps) {
  const { t } = useTranslation('site-builder-blocks');
  const parsed = parseVideoUrl(url, enableTracking);
  const accessibleTitle = title ?? t('embeds.video.title');

  useEffect(() => {
    if (parsed.provider === 'cinema8') {
      ensureCinema8PlayerScript();
    }
  }, [parsed.provider]);

  const isCinema8Popover = parsed.provider === 'cinema8' && display === 'popover';

  const css = `
    .lms-vid { margin: 0; display: block; }
    .lms-vid__frame {
      position: relative; width: 100%;
      aspect-ratio: ${aspectRatio};
      background: #000;
      border-radius: var(--radius-md, 8px);
      overflow: hidden;
    }
    .lms-vid__frame iframe,
    .lms-vid__frame cinema8-player {
      position: absolute; inset: 0;
      width: 100%; height: 100%;
      border: 0; display: block;
    }
    .lms-vid--popover .lms-vid__frame {
      aspect-ratio: auto;
      position: static;
      background: transparent;
      border-radius: 0;
      overflow: visible;
    }
    .lms-vid--popover .lms-vid__frame cinema8-player {
      position: static;
      width: 100%; height: auto;
    }
    .lms-vid__fallback {
      position: absolute; inset: 0;
      display: flex; align-items: center; justify-content: center;
      color: #fff; font-size: 14px;
      text-decoration: underline; gap: 8px;
    }
    .lms-vid__caption {
      margin-top: 10px;
      font-size: 13px; line-height: 1.5;
      color: var(--color-text-secondary, #71717a);
    }
  `;

  const cinema8HostRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const host = cinema8HostRef.current;
    if (!host) return;
    if (parsed.provider !== 'cinema8' || !parsed.mediaId) {
      host.innerHTML = '';
      return;
    }
    const safeId = parsed.mediaId.replace(/[<>"']/g, '');
    if (isCinema8Popover) {
      host.innerHTML = `<cinema8-player media-id="${safeId}" display="thumbnail" cinema8-popover></cinema8-player>`;
    } else {
      host.innerHTML = `<cinema8-player media-id="${safeId}"></cinema8-player>`;
    }
  }, [parsed.provider, parsed.mediaId, isCinema8Popover]);

  return (
    <BlockRoot as="figure" className={`lms-vid${isCinema8Popover ? ' lms-vid--popover' : ''}`}>
      <style dangerouslySetInnerHTML={{ __html: css }} />
      <div className="lms-vid__frame">
        {parsed.provider === 'cinema8' && parsed.mediaId ? (
          <div ref={cinema8HostRef} suppressHydrationWarning />
        ) : parsed.embedUrl ? (
          <iframe
            src={parsed.embedUrl}
            title={accessibleTitle}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            sandbox="allow-scripts allow-presentation allow-same-origin"
            loading="lazy"
            referrerPolicy={enableTracking ? 'strict-origin-when-cross-origin' : 'no-referrer'}
          />
        ) : parsed.watchUrl ? (
          <a
            className="lms-vid__fallback"
            href={parsed.watchUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            {t('embeds.video.watch')}
          </a>
        ) : (
          <div className="lms-vid__fallback" aria-hidden="true">
            {t('embeds.video.noUrl')}
          </div>
        )}
      </div>
      {caption && <figcaption className="lms-vid__caption">{caption}</figcaption>}
    </BlockRoot>
  );
}
